import Vue from 'vue';

// Api
import DeliveryApi from '@/api/DeliveryApi';

// IDB
import MailServices from '@/idb/MailServices';

// Utils
import DataUtil from '@/utils/DataUtil';
import ValidationUtil from '@/utils/ValidationUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

const actionTypes = {
    reset: 'reset',
    clearItemLists: 'clearItemLists',
    picklist: {
        transformAndSetPicklistItems: 'transformAndSetPicklistItems',
        clearPicklistItems: 'clearPicklistItems',
    },
    scanned: {
        addScannedItem: 'addScannedItem',
        removeScannedItemsByDestinationLocatorId: 'removeScannedItemsByDestinationLocatorId',
        clearScannedItems: 'clearScannedItems',
    },
    delivery: {
        setCurrentDestination: 'setCurrentDestination',
        clearCurrentDestination: 'clearCurrentDestination',
        deliverMailItem: 'deliverMailItem',
        clearSuccessfulDeliveries: 'clearSuccessfulDeliveries',
    },
};
const mutationTypes = {
    picklist: {
        SET_PICKLIST_ITEMS: 'SET_PICKLIST_ITEMS',
        UPDATE_DELIVERED_PICKLIST_ITEM_LIST: 'UPDATE_DELIVERED_PICKLIST_ITEM_LIST',
    },
    scanned: {
        SET_SCANNED_ITEMS: 'SET_SCANNED_ITEMS',
        ADD_SCANNED_ITEM: 'ADD_SCANNED_ITEM',
        REMOVE_SCANNED_ITEMS_BY_DESTINATION_LOCATOR_ID: 'REMOVE_SCANNED_ITEMS_BY_DESTINATION_LOCATOR_ID',
    },
    delivery: {
        SET_CURRENT_DESTINATION: 'SET_CURRENT_DESTINATION',
        SET_DELIVERY_IN_FLIGHT: 'SET_DELIVERY_IN_FLIGHT',
        SET_SUCCESSFUL_DELIVERIES: 'SET_SUCCESSFUL_DELIVERIES',
        ADD_SUCCESSFUL_DELIVERY_LIST: 'ADD_SUCCESSFUL_DELIVERY_LIST',
    },
};

/*
* Notes:
* All items that can be delivered are first put into picklist.items
* Then as those items are scanned to delivery.currentDestination, they are copied into scanned.items
* When 'delivering', we deliver all scanned.items for a given destinationLocatorId, and then copy those mailItemId's into delivery.successfulDeliveries
*/
export default {
    namespaced: true,
    state: {
        /** @type { Object } - Info related the the list of items the user is attempting to deliver */
        picklist: {
            /**
             * @type { Object } - A list of mail items keyed by id
             * The current working list of items that the user is attempting to deliver
             */
            items: {},
        },
        /** @type { Object } - Info related to items that have been scanned */
        scanned: {
            /**
             * @type { Object } - A list of refNums that have been scanned to a destinationLocatorId, but are yet to be delivered
             *  {
             *      123: { // keyed by destination location id
             *          destinationLocatorId: 123,
             *          refNums: [], // { String[] } of refNums scanned to the destinationLocationId
             *      }
             *  }
             */
            items: {},
        },
        /** @type { Object } - Info related to delivering the list of items in the picklist */
        delivery: {
            /** @type { Object } */
            currentDestination: {
                /** @type { String } */
                locatorId: '',
                /** @type { String } */
                name: '',
            },
            /** @type { Boolean } */
            inFlight: false,
            /** @type { Array } - of delivered mail item id's */
            successfulDeliveries: [],
        },
    },
    getters: {
        /** @returns { Array } */
        picklistItems: ( state ) => Object.values( state.picklist.items ),
        /** @returns { Boolean } */
        hasPicklistItems: ( state, getters ) => ( getters.picklistItems.length > 0 ),
        /** @returns { Function } -> Boolean */
        itemIsInPicklistByRefNum: ( state, getters ) => ( refNum ) => (
            getters.picklistItems
                .map( ( item ) => item.refNum.toString() )
                .includes( refNum.toString() )
        ),
        /** @returns { Function } -> String */
        getMailItemIdForRefNum: ( state, getters ) => ( refNum ) => (
            ( getters.hasPicklistItems && getters.itemIsInPicklistByRefNum( refNum ) )
                ? getters.picklistItems
                    .find( ( item ) => item.refNum.toString() === refNum.toString() )
                    .id
                : ''
        ),
        /** @returns { Function } -> Boolean */
        itemIsInPicklistByTrackingNumber: ( state, getters ) => ( trackingNumber ) => (
            getters.picklistItems
                .filter( ( item ) => !!item.trackingNumber ) // filter out null trackingNumbers
                .map( ( item ) => item.trackingNumber )
                .includes( trackingNumber.toString() )
        ),
        /** @returns { Function } -> String */
        getMailItemRefByTrackingNumber: ( state, getters ) => ( trackingNumber ) => (
            ( getters.hasPicklistItems && getters.itemIsInPicklistByTrackingNumber( trackingNumber ) )
                ? getters.picklistItems
                    .filter( ( item ) => !!item.trackingNumber ) // filter out null trackingNumbers
                    .find( ( item ) => item.trackingNumber.toString() === trackingNumber.toString() )
                    .refNum
                : ''
        ),
        /** @returns { Function } -> String */
        getMailItemRefNumFromRefNumOrTrackingNumber: ( state, getters ) => ( refNumOrTrackingNumber ) => {
            if ( getters.itemIsInPicklistByRefNum( refNumOrTrackingNumber ) ) return refNumOrTrackingNumber;
            if ( getters.itemIsInPicklistByTrackingNumber( refNumOrTrackingNumber ) ) return getters.getMailItemRefByTrackingNumber( refNumOrTrackingNumber );
            return '';
        },

        /** @returns { Array } */
        scannedItemList: ( state ) => Object.values( state.scanned.items ),
        /** @returns { Boolean } */
        hasScannedItems: ( state, getters ) => ( getters.scannedItemList.length > 0 ),
        /** @returns { Function } -> Array */
        getMailItemIdListFromScannedItemsByDestinationLocationId: ( state, getters ) => ( destinationLocatorId ) => (
            ( getters.hasScannedItems && state.scanned.items[ destinationLocatorId ] && state.scanned.items[ destinationLocatorId ].refNums )
                ? state.scanned.items[ destinationLocatorId ].refNums
                    .map( getters.getMailItemIdForRefNum )
                    .filter( ( item ) => !!item )
                    // filter out items that have been successfully delivered
                    .filter( ( itemId ) => !state.delivery.successfulDeliveries.includes( itemId ) )
                : []
        ),
        /** @returns { Array } */
        getMailItemIdListFromScannedItemsForCurrentDestination: ( state, getters ) => (
            getters.getMailItemIdListFromScannedItemsByDestinationLocationId( state.delivery.currentDestination.locatorId )
        ),
        /** @returns { Boolean } */
        hasMailItemsToBeDeliveredToCurrentDestination: ( state, getters ) => (
            getters.getMailItemIdListFromScannedItemsForCurrentDestination.length > 0
        ),
        /** @returns { Boolean } */
        hasCurrentDestinationLocatorId: ( state ) => !!state.delivery.currentDestination.locatorId,
        /** @returns { Function } -> String */
        getDestinationNameFromPicklistByLocatorId: ( state, getters ) => ( locatorId ) => (
            getters.hasPicklistItems
            && getters.picklistItems
                .map( ( item ) => item.destinationLocatorId )
                .includes( Number( locatorId ) )
                ? getters.picklistItems
                    .find( ( item ) => item.destinationLocatorId === Number( locatorId ) )
                    .destinationLocatorDescription
                : ''
        ),
        /** @returns { Array } - of deliveryItems with a few added attributes for display purposes */
        transformedDeliverableList: ( state, getters ) => (
            getters.picklistItems.map( ( item ) => ( {
                ...item,
                // Add a 'scanned' field so we can show it as a checkbox in the interface
                scanned: (
                    getters.scannedItemList
                        .flatMap( ( scannedItem ) => scannedItem.refNums )
                        .includes( item.refNum )
                    || !!item.dateDelivered
                ),
                // Add a 'delivered' field so we can color the checkbox if we've successfully POSTed our delivery to the backend
                delivered: ( state.delivery.successfulDeliveries.includes( item.id.toString() ) || !!item.dateDelivered ),
            } ) )
        ),
        /** @returns { Array } - either the full list of delivery items, or a truncated list based on the currentDestination.locatorId */
        displayedDeliverableList: ( state, getters ) => (
            state.delivery.currentDestination.locatorId
                ? getters.transformedDeliverableList
                    .filter( ( item ) => (
                        !!item.destinationLocatorId
                        && ( item.destinationLocatorId.toString() === state.delivery.currentDestination.locatorId.toString() )
                    ) )
                    // filter out items that have been successfully delivered
                    .filter( ( item ) => !state.delivery.successfulDeliveries.includes( item.id ) )
                : getters.transformedDeliverableList
        ),
        /** @returns { Function } -> { Boolean } */
        isItemInDisplayedDeliverableList: ( state, getters ) => ( scanValue ) => (
            getters.displayedDeliverableList.some( ( item ) => ( item.refNum === scanValue ) || item.trackingNumber === scanValue )
        ),
        /** @returns { Array } of { Objects } - [ { name: 'destinationLocatorDescription', locatorId: 'destinationLocatorId' } ] */
        distinctDropOffLocations: ( state, getters ) => (
            [ ...new Set( getters.transformedDeliverableList.map( ( item ) => item.destinationLocatorId ) ) ]
                .map( ( id ) => ( {
                    name: getters.getDestinationNameFromPicklistByLocatorId( id ),
                    locatorId: id,
                } ) )
        ),
        /** @returns { Function } -> Array - an array of items for a given destinationLocatorId */
        getDeliverableItemsForDropOffLocation: ( state, getters ) => ( destinationLocatorId ) => (
            getters.transformedDeliverableList.filter( ( item ) => item.destinationLocatorId === destinationLocatorId )
        ),
        /**
         * @returns { Array } of distinctDropOffLocators { Objects }
         *  with that drop off locator's associated items, sorted desc destinationLocatorDescription
         */
        deliverableItemsGroupedByDropOffLocation: ( state, getters ) => (
            getters.distinctDropOffLocations
                .map( ( destination ) => ( {
                    ...destination,
                    items: getters.getDeliverableItemsForDropOffLocation( destination.locatorId )
                        // filter out items that have been successfully delivered
                        .filter( ( item ) => !state.delivery.successfulDeliveries.includes( item.id ) ),
                } ) )
                .filter( ( destinationWithItems ) => destinationWithItems.items.length > 0 )
                .sort( ( a, b ) => ( `${ b.destinationLocatorDescription }` ).localeCompare( a.destinationLocatorDescription ) )
                // .sort( ( a, b ) => ( a.items.length < b.items.length ? 1 : -1 ) )
        ),
    },
    actions: {
        [ actionTypes.reset ]( { dispatch } ) {
            dispatch( actionTypes.clearItemLists );
            dispatch( actionTypes.delivery.clearCurrentDestination );
        },

        async [ actionTypes.clearItemLists ]( context ) {
            await context.dispatch( actionTypes.picklist.clearPicklistItems );
            await context.dispatch( actionTypes.scanned.clearScannedItems );
            await context.dispatch( actionTypes.delivery.clearSuccessfulDeliveries );
        },

        /**
         * Picklist Actions
         */
        [ actionTypes.picklist.transformAndSetPicklistItems ]( context, { list } ) {
            context.commit( mutationTypes.picklist.SET_PICKLIST_ITEMS, {
                keyedList: ( ValidationUtil.arrayHasItems( list ) && ( typeof list !== 'string' ) )
                    ? DataUtil.normalize( list, 'id' )
                    : {},
            } );
        },
        [ actionTypes.picklist.clearPicklistItems ]( context ) {
            context.commit( mutationTypes.picklist.SET_PICKLIST_ITEMS, { keyedList: {} } );
        },

        /**
         * Scanned Actions
         */
        [ actionTypes.scanned.addScannedItem ]( context, { item, destinationLocatorId } ) {
            context.commit( mutationTypes.scanned.ADD_SCANNED_ITEM, { item, destinationLocatorId } );
        },
        [ actionTypes.scanned.removeScannedItemsByDestinationLocatorId ]( context, { destinationLocatorId } ) {
            context.commit( mutationTypes.scanned.REMOVE_SCANNED_ITEMS_BY_DESTINATION_LOCATOR_ID, { destinationLocatorId } );
        },
        [ actionTypes.scanned.clearScannedItems ]( context ) {
            context.commit( mutationTypes.scanned.SET_SCANNED_ITEMS, { list: {} } );
        },

        /**
         * Delivery Actions
         */
        async [ actionTypes.delivery.setCurrentDestination ]( context, { locatorId, name } ) {
            context.commit( mutationTypes.delivery.SET_CURRENT_DESTINATION, { locatorId, name } );
        },
        [ actionTypes.delivery.clearCurrentDestination ]( context ) {
            context.commit( mutationTypes.delivery.SET_CURRENT_DESTINATION, {
                locatorId: '',
                name: '',
            } );
        },
        [ actionTypes.delivery.clearSuccessfulDeliveries ]( context ) {
            context.commit( mutationTypes.delivery.SET_SUCCESSFUL_DELIVERIES, { list: [] } );
        },
        async [ actionTypes.delivery.deliverMailItem ]( context, {
            mailItemIdList,
            locatorId,
            notes = '',
            photos = [],
            isReplay = false,
        } ) {
            context.commit( mutationTypes.delivery.SET_DELIVERY_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await DeliveryApi.deliverMailItem( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    mailItemIdList,
                    locatorId: Number( locatorId ),
                    notes,
                    photos,
                } );
                // Update our lists & clear our scanned items
                if ( ValidationUtil.arrayHasItems( data.mailTransWithMailTransItemsDTOList ) ) {
                    // Only beep if we're not re-attempting this action
                    if ( !isReplay ) {
                        await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
                    }
                    context.commit( mutationTypes.delivery.ADD_SUCCESSFUL_DELIVERY_LIST, { itemList: mailItemIdList } );
                    context.commit( mutationTypes.picklist.UPDATE_DELIVERED_PICKLIST_ITEM_LIST, {
                        idList: mailItemIdList,
                        locatorDescription: data.mailTransWithMailTransItemsDTOList[ 0 ].mailTransItems[ 0 ].destinationName,
                        timestamp: ( new Date() ).getTime(),
                    } );
                }
            } catch ( error ) {
                // Only beep if we're not re-attempting this action
                if ( !isReplay ) {
                    await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
                }
                // If message come from backend, show error to the user
                // If message does not come from backend, there's no WiFi,
                // So explain the situation to the user.
                context.commit( mutationTypes.picklist.UPDATE_DELIVERED_PICKLIST_ITEM_LIST, {
                    idList: mailItemIdList,
                    locatorDescription: ( error.response && error.response.data && error.response.data.message )
                        ? `Error: Unable to Deliver: ${ error.response.data.message }`
                        : 'Notice: No connection. Delivery will be completed when connection is restored.',
                    timestamp: null,
                } );
                // Store an attempted delivery to retry later
                await MailServices.storeNewAction( 'Mail/Delivery/Current/deliverMailItem', {
                    mailItemIdList,
                    locatorId,
                    notes,
                    photos,
                } );
                throw error;
            } finally {
                context.commit( mutationTypes.delivery.SET_DELIVERY_IN_FLIGHT, { boolean: false } );
            }
        },
    },
    mutations: {
        /**
         * Picklist Mutations
         */
        /** @params keyedList { Object } - a normalized list of items */
        [ mutationTypes.picklist.SET_PICKLIST_ITEMS ]( state, { keyedList } ) {
            state.picklist.items = keyedList;
        },
        [ mutationTypes.picklist.UPDATE_DELIVERED_PICKLIST_ITEM_LIST ]( state, { idList, locatorDescription, timestamp } ) {
            idList.forEach( ( id ) => {
                if ( state.picklist.items[ id ] ) {
                    state.picklist.items[ id ].locatorDescription = locatorDescription;
                    state.picklist.items[ id ].dateDelivered = timestamp;
                }
            } );
        },

        /**
         * Scanned Mutations
         */
        [ mutationTypes.scanned.SET_SCANNED_ITEMS ]( state, { list } ) {
            state.scanned.items = list;
        },
        [ mutationTypes.scanned.ADD_SCANNED_ITEM ]( state, { item, destinationLocatorId } ) {
            if ( state.scanned.items[ destinationLocatorId ] ) {
                // Prevent a given refNum from being added multiple times to a destinationLocatorId
                if ( !state.scanned.items[ destinationLocatorId ].refNums.includes( item ) ) {
                    Vue.set(
                        state.scanned.items[ destinationLocatorId ],
                        'refNums',
                        state.scanned.items[ destinationLocatorId ].refNums.concat( item ),
                    );
                }
            } else {
                Vue.set(
                    state.scanned.items,
                    destinationLocatorId,
                    { destinationLocatorId, refNums: [ item ] },
                );
            }
        },
        [ mutationTypes.scanned.REMOVE_SCANNED_ITEMS_BY_DESTINATION_LOCATOR_ID ]( state, { destinationLocatorId } ) {
            if ( state.scanned.items[ destinationLocatorId ] ) {
                Vue.delete(
                    state.scanned.items,
                    destinationLocatorId,
                );
            }
        },

        /**
         * Delivery Mutations
         */
        [ mutationTypes.delivery.SET_DELIVERY_IN_FLIGHT ]( state, { boolean } ) {
            state.delivery.inFlight = boolean;
        },
        [ mutationTypes.delivery.SET_CURRENT_DESTINATION ]( state, { locatorId, name } ) {
            state.delivery.currentDestination.locatorId = locatorId;
            state.delivery.currentDestination.name = name;
        },
        [ mutationTypes.delivery.SET_SUCCESSFUL_DELIVERIES ]( state, { list } ) {
            state.delivery.successfulDeliveries = list;
        },
        [ mutationTypes.delivery.ADD_SUCCESSFUL_DELIVERY_LIST ]( state, { itemList } ) {
            state.delivery.successfulDeliveries = state.delivery.successfulDeliveries.concat( itemList );
        },
    },
};
